<template>
  <div :class="wrapperClassName" v-if="visibleIfResult">
    <template v-if="!hidden">
      <label :class="labelClassName" :for="idInput" v-dompurify-html="labelText"></label>
      <span class="wdw-dynamic-label-addition">
        <slot name="dynamicLabel"></slot>
      </span>
      <span class="help-trigger" title="Hilfe anzeigen" @click="toggleHelpText" v-if="help">
        <font-awesome-icon icon="question-circle" size="lg" />
      </span>
      <Message
        v-show="shouldShowHelp"
        closable
        severity="info"
        role="alert"
        class="mb-2 form-info-message"
        @close="toggleHelpText"
      >
        <span v-dompurify-html="help"></span>
      </Message>
    </template>

    <span v-show="hasMaxLength" class="mx-2">{{ maxLengthStateText }}</span>
    <Password
      v-if="inputType == `password`"
      :inputId="idInput"
      :class="`form-control`"
      :inputClass="inputClassName"
      class="password"
      v-model="value"
      :feedback="false"
      :toggleMask="inputClassName['is-invalid'] ? false : true"
      :aria-invalid="hasError || hasServerError"
      :data-vv-as="labelText"
      :name="name"
      :required="required"
      :placeholder="placeholder"
      :pattern="pattern"
      ref="input"
      :data-wdw-code="code"
    />

    <input
      v-else
      :id="idInput"
      :data-wdw-code="code"
      ref="input"
      :type="inputType"
      :data-vv-as="labelText"
      :name="name"
      :required="required"
      :placeholder="placeholder"
      :pattern="pattern"
      :class="inputClassName"
      v-model="value"
      tabindex="0"
      :aria-invalid="hasError || hasServerError"
      :aria-describedby="idErrorText"
      :hidden="hidden"
      :disabled="disabled"
      @keyup="onTextEntered($event)"
    />
    <template v-if="!hidden">
      <span :id="idErrorText" v-show="hasError" class="text-danger form-error-message" aria-atomic="true">
        {{ error }}
      </span>
      <span :id="idErrorText" v-show="hasServerError" class="text-danger form-error-message" aria-atomic="true">
        {{ serverError }}
      </span>
      <span v-if="hasError && inputType == `password`">Bitte geben Sie ein Password ein</span>
      <span v-show="warnOverlimit" class="text-warning">Maximale Zeichenanzahl erreicht</span>
    </template>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, toRefs, compile } from 'vue';
import { useField } from 'vee-validate';
import useInputValidate from '@/composables/general/useInputValidate';
import useToggler from '@/composables/general/useToggler';
import Password from 'primevue/password';
import Message from 'primevue/message';
// TODO -> refactor, test  & style

export default {
  name: 'form-control-input',

  props: {
    name: {
      required: true,
      type: String,
    },
    code: {
      required: false,
      type: String,
      default: '',
    },
    label: {
      required: false,
      type: String,
      default: '',
    },
    placeholder: {
      required: false,
      type: String,
      default: '',
    },
    type: {
      required: false,
      type: String,
      default: 'text',
    },
    cachevalue: {
      required: false,
      type: String,
      default: '',
    },
    defaultvalue: {
      required: false,
      type: String,
      default: '',
    },
    additionalLabelClasses: {
      required: false,
      type: String,
      default: '',
    },
    required: {
      required: false,
      type: Boolean,
      default: false,
    },
    error: {
      required: false,
      type: String,
      default: '',
    },
    serverError: {
      required: false,
      type: String,
      default: '',
    },
    help: {
      required: false,
      type: String,
      default: '',
    },
    shouldcache: {
      required: false,
      type: Boolean,
      default: false,
    },
    hidden: {
      required: false,
      type: Boolean,
      default: false,
    },
    pattern: {
      required: false,
      type: String,
      default: '',
    },
    repeatFromAnotherInputId: {
      required: false,
      type: String,
      default: '',
    },
    prefixId: {
      required: false,
      type: String,
      default: 'wdw-bms-attr',
    },
    parentFormId: {
      required: false,
      type: String,
      default: '',
    },
    visibleIfResult: {
      required: false,
      type: Boolean,
      default: true,
    },
    isPublic: {
      type: Boolean,
      default: true,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
    maxlength: {
      // 0 means no limit
      required: false,
      type: Number,
      default: 0,
    },
    reload: {
      // If the counter is increased, it will automatically empty the value of this input field
      required: false,
      type: Number,
      default: 0,
    },
    overridevalue: {
      // Use this to override value (e.g. when parent wants to change the value of this component)
      // Only triggered if the overridden value is different than actual value
      required: false,
      type: String,
      default: '',
    },
  },

  components: {
    Password,
    Message,
  },
  emits: ['validate'],

  setup(props, context) {
    const {
      name,
      code,
      label,
      type,
      required,
      defaultvalue,
      error,
      serverError,
      additionalLabelClasses,
      shouldcache,
      repeatFromAnotherInputId,
      prefixId,
      isPublic,
      maxlength,
    } = toRefs(props);

    const { isValidEmail, isValidURL } = useInputValidate();
    const { toggleValue: shouldShowHelp, toggle: toggleHelpText } = useToggler();
    const warnOverlimit = ref(false);
    const remainingCount = ref(524800);

    //+---------------------+
    //|    Other functions  |
    //+---------------------+

    function validate(value) {
      // console.log(type.value);
      // vee-validate's composition API
      const finalvalue = value ? value.trim() : '';
      const returnValue = {
        value: finalvalue,
        valueHuman: finalvalue,
        validated: true,
        name: name.value,
        code: code.value,
        type: type.value,
      };
      if (required.value || (finalvalue !== '' && finalvalue !== defaultvalue.value)) {
        const inptype = type.value;
        if (inptype == 'number') {
          returnValue.validated = !isNaN(finalvalue);
        } else if (inptype == 'email') {
          returnValue.validated = isValidEmail(finalvalue);
        } else if (inptype == 'link') {
          returnValue.validated = isValidURL(finalvalue);
        } else if (repeatFromAnotherInputId.value !== '') {
          const anotherInp = document.getElementById(repeatFromAnotherInputId.value);
          if (anotherInp)
            returnValue.validated = anotherInp.value !== '' && finalvalue !== '' && anotherInp.value == finalvalue;
          else returnValue.validated = true;
        } else {
          const withinMaxLength = maxlength.value > 0 ? finalvalue.length <= maxlength.value : true;
          returnValue.validated = finalvalue !== '' && withinMaxLength;
        }
      }

      if (shouldcache.value) {
        localStorage.setItem(name.value, returnValue.value);
      }

      // Emit event
      context.emit('validate', returnValue);
      return returnValue.validated ? true : error.value;
    }
    const { errorMessage, value } = useField(name, validate, {
      initialValue: defaultvalue.value,
    });

    function onTextEntered(event) {
      // Show warning on limit being reached
      if (maxlength.value > 0) {
        remainingCount.value = maxlength.value - value.value.length;
        warnOverlimit.value = remainingCount.value <= 0;
      }
    }

    //+---------------------+
    //|    computed()       |
    //+---------------------+
    const hasError = computed(() => {
      return errorMessage.value != undefined;
    });
    const hasServerError = computed(() => {
      return serverError.value != undefined && serverError.value !== '';
    });
    const wrapperClassName = computed(() => {
      return {
        'form-group': true,
        'has-error': hasError.value || hasServerError.value,
      };
    });
    const labelClassName = computed(() => {
      return additionalLabelClasses.value + ' ' + (hasError.value || hasServerError.value ? 'text-danger' : '');
    });
    const inputClassName = computed(() => {
      // refactor to string to be able to provide class-names to Password-component from primevue (expects string)
      let classes = 'form-control';
      if (hasError.value || hasServerError.value) {
        classes += ' is-invalid';
      }
      return classes;
    });

    const labelText = computed(() => {
      return (
        label.value +
        (required.value ? '*' : '') +
        (isPublic.value ? '' : ' - <span class="text-muted">Diese Daten werden nicht veröffentlicht.</span>')
      );
    });
    const idInput = computed(() => {
      return prefixId.value + '-text-' + name.value;
    });
    const idErrorText = computed(() => {
      return prefixId.value + '-text-' + name.value + '-error';
    });
    const inputType = computed(() => {
      const typeVal = type.value;
      if (typeVal == 'number') {
        return 'number';
      } else if (typeVal == 'email') {
        return 'email';
      } else if (typeVal == 'link') {
        return 'url';
      } else if (typeVal == 'password') {
        return 'password';
      }
      return 'text';
    });
    const hasMaxLength = computed(() => {
      return maxlength.value > 0 && inputType.value == 'text';
    });
    const maxLengthStateText = computed(() => {
      const remaining = value.value ? maxlength.value - value.value.length : maxlength.value;
      return remaining + ' / ' + maxlength.value + ' Zeichen verfügbar';
    });

    onMounted(() => {
      const inputField = document.getElementById(idInput.value);
      if (inputField && hasMaxLength.value) {
        inputField.setAttribute('maxlength', maxlength.value);
      }
    });

    //+---------------------+
    //|  Watcher            |
    //+---------------------+
    watch(
      () => props.reload,
      (nv, ov) => {
        if (nv > ov) {
          value.value = defaultvalue.value;
        }
      }
    );
    watch(
      () => props.overridevalue,
      (nv, ov) => {
        if (nv != ov && nv != value.value) {
          value.value = nv;
        }
      }
    );

    //+---------------------+
    //|  Lifecycle Hooks    |
    //+---------------------+
    //+---------------------+
    //|    return data      |
    //+---------------------+
    return {
      // Computed
      hasError,
      hasMaxLength,
      maxLengthStateText,
      hasServerError,
      wrapperClassName,
      labelClassName,
      inputClassName,
      idInput,
      idErrorText,
      inputType,
      labelText,
      // Methods
      toggleHelpText,
      onTextEntered,
      // Variables
      shouldShowHelp,
      errorMessage,
      value,
      warnOverlimit,
      remainingCount,
    };
  },
};
</script>

<style lang="scss" scoped>
label {
  display: inline;
  font-weight: bold;
  max-width: 100%;
}
input {
  margin-top: 5px;
}
.form-group {
  margin-bottom: 15px;
}
.help-trigger {
  cursor: pointer;
  margin-left: 4px;
}
.password {
  margin-top: 5px;
  display: block;
  &.form-control {
    padding: 0 0;
    position: relative;
  }
}
</style>
